import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { FaUser, FaTrash, FaEdit } from 'react-icons/fa';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import apiService from '../../../services/apiService';
import { Country, State, City } from 'country-state-city';

function AdminBidPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useContext(AuthContext);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollectorModalOpen, setIsCollectorModalOpen] = useState(false);
  const [collectorDetails, setCollectorDetails] = useState(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    street: '',
    postalCode: '',
    country: '',
    state: '',
    city: '',
    active: true,
    isVerified: false,
  });

  const fetchBids = async () => {
    try {
      const data = await apiService.get(`/waste/bids/${requestId}`);
      setBids(data);
    } catch (err) {
      console.error('Error fetching bids:', err);
      setError(err);
      toast.error('Failed to load bids');
      navigate('/waste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied. Admins only.');
      navigate('/login');
      return;
    }
    if (!requestId) {
      toast.error('Invalid request ID');
      navigate('/waste');
      return;
    }
    fetchBids();
  }, [requestId, navigate, isAdmin]);

  const handleAcceptBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'accepted' });
      toast.success('Bid accepted successfully');
      fetchBids();
    } catch (err) {
      console.error('Accept bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to accept bid');
    }
  };

  const handleDeclineBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'declined' });
      toast.success('Bid declined successfully');
      fetchBids();
    } catch (err) {
      console.error('Decline bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to decline bid');
    }
  };

  const handlePendingBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'pending' });
      toast.success('Bid set to pending successfully');
      fetchBids();
    } catch (err) {
      console.error('Pending bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to set bid to pending');
    }
  };

  const handleCompleteBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'completed' });
      toast.success('Bid marked as completed successfully');
      fetchBids();
    } catch (err) {
      console.error('Complete bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to mark bid as completed');
    }
  };

  const handleCancelBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'canceled' });
      toast.success('Bid canceled successfully');
      fetchBids();
    } catch (err) {
      console.error('Cancel bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to cancel bid');
    }
  };

  const handleDeleteBid = async (bidId) => {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      try {
        await apiService.delete(`/waste/bid/${requestId}/${bidId}`);
        toast.success('Bid deleted successfully');
        fetchBids();
      } catch (err) {
        console.error('Delete bid error:', err);
        toast.error('Failed to delete bid');
      }
    }
  };

  const handleViewCollector = async (collectorId) => {
    if (!collectorId) {
      toast.error('Invalid collector ID');
      return;
    }
    try {
      const data = await apiService.get(`/users/${collectorId}`);
      setCollectorDetails(data);
      setForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        street: data.street || '',
        postalCode: data.postalCode || '',
        country: data.country || '',
        state: data.state || '',
        city: data.city || '',
        active: data.active,
        isVerified: data.isVerified,
      });
      setIsCollectorModalOpen(true);
    } catch (err) {
      console.error('Error fetching collector details:', err);
      toast.error('Failed to load collector details');
    }
  };

  const handleUpdateCollector = async (e) => {
    e.preventDefault();
    if (!collectorDetails?._id) {
      toast.error('Invalid collector data');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('firstName', form.firstName);
      formData.append('lastName', form.lastName);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('address', form.address);
      formData.append('street', form.street);
      formData.append('postalCode', form.postalCode);
      formData.append('country', form.country);
      formData.append('state', form.state);
      formData.append('city', form.city);
      formData.append('active', form.active);
      formData.append('isVerified', form.isVerified);

      await apiService.upload(`/users/admin/${collectorDetails._id}`, formData, { method: 'PUT' });
      toast.success('Collector updated successfully');
      setIsCollectorModalOpen(false);
      setCollectorDetails(null);
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        street: '',
        postalCode: '',
        country: '',
        state: '',
        city: '',
        active: true,
        isVerified: false,
      });
    } catch (err) {
      console.error('Update collector error:', err);
      toast.error(err.response?.data?.error || 'Failed to update collector');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const handleCountryChange = (selected) => {
    const newCountry = selected ? selected.value : '';
    setForm((prev) => ({ ...prev, country: newCountry, state: '', city: '' }));
  };

  const handleStateChange = (selected) => {
    const newState = selected ? selected.value : '';
    setForm((prev) => ({ ...prev, state: newState, city: '' }));
  };

  const handleCityChange = (selected) => {
    setForm((prev) => ({ ...prev, city: selected ? selected.value : '' }));
  };

  const countryOptions = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  const stateOptions = form.country
    ? State.getStatesOfCountry(form.country).map((s) => ({
        value: s.isoCode,
        label: s.name,
      }))
    : [];

  const cityOptions = form.country && form.state
    ? City.getCitiesOfState(form.country, form.state).map((c) => ({
        value: c.name,
        label: c.name,
      }))
    : [];

  const bidColumns = [
    {
      key: 'collectorId',
      label: 'Collector',
      render: (b) => (
        <button
          onClick={() => handleViewCollector(b.collectorId?._id || b.collectorId)}
          className="text-blue-500 hover:text-blue-600 underline"
          title="View Collector Details"
        >
          {`${b.collectorId?.firstName || ''} ${b.collectorId?.lastName || ''}`}
        </button>
      ),
    },
    { key: 'amount', label: 'Bid Amount ($)' },
    { key: 'note', label: 'Note' },
    { key: 'status', label: 'Status' },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (b) => new Date(b.createdAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (b) => (
        <div className="flex space-x-2">
          {b.status === 'pending' ? (
            <>
              <button
                onClick={() => handleAcceptBid(b._id)}
                className="text-green-500 hover:text-green-600"
                title="Accept Bid"
              >
                Accept
              </button>
              <button
                onClick={() => handlePendingBid(b._id)}
                className="text-yellow-500 hover:text-yellow-600"
                title="Set to Pending"
              >
                Pending
              </button>
              <button
                onClick={() => handleDeclineBid(b._id)}
                className="text-red-500 hover:text-red-600"
                title="Decline Bid"
              >
                Decline
              </button>
              <button
                onClick={() => handleDeleteBid(b._id)}
                className="text-red-500 hover:text-red-600"
                title="Delete Bid"
              >
                <FaTrash />
              </button>
            </>
          ) : b.status === 'accepted' ? (
            <>
              <button
                onClick={() => handleCompleteBid(b._id)}
                className="text-blue-500 hover:text-blue-600"
                title="Mark as Completed"
              >
                Completed
              </button>
              <button
                onClick={() => handlePendingBid(b._id)}
                className="text-yellow-500 hover:text-yellow-600"
                title="Set to Pending"
              >
                Pending
              </button>
              <button
                onClick={() => handleCancelBid(b._id)}
                className="text-red-500 hover:text-red-600"
                title="Cancel Bid"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => handlePendingBid(b._id)}
              className="text-yellow-500 hover:text-yellow-600"
              title="Set to Pending"
            >
              Pending
            </button>
          )}
          <button
            onClick={() => handleViewCollector(b.collectorId?._id || b.collectorId)}
            className="text-blue-500 hover:text-blue-600"
            title="View Collector"
          >
            <FaUser />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Bids for Waste Request #{requestId}
        </h2>
        <button
          onClick={() => navigate('/waste')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Waste Management
        </button>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">Loading...</div>
      ) : error ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">Error: {error}</div>
      ) : bids && bids.length > 0 ? (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={bidColumns} data={bids} />
        </div>
      ) : (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No bids found for this request.
        </div>
      )}
      <Modal
        isOpen={isCollectorModalOpen}
        onClose={() => {
          setIsCollectorModalOpen(false);
          setCollectorDetails(null);
          setForm({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            street: '',
            postalCode: '',
            country: '',
            state: '',
            city: '',
            active: true,
            isVerified: false,
          });
        }}
        title="Collector Details"
        className="max-w-5xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-6">
          {collectorDetails ? (
            <form onSubmit={handleUpdateCollector} className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FormInput
                      label="First Name"
                      name="firstName"
                      type="text"
                      value={form.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Last Name"
                      name="lastName"
                      type="text"
                      value={form.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <Select
                      options={countryOptions}
                      value={countryOptions.find((opt) => opt.value === form.country) || null}
                      onChange={handleCountryChange}
                      placeholder="Select a country"
                      isClearable
                      className="w-full"
                      classNamePrefix="react-select"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <Select
                      options={stateOptions}
                      value={stateOptions.find((opt) => opt.value === form.state) || null}
                      onChange={handleStateChange}
                      placeholder="Select a state"
                      isClearable
                      isDisabled={!form.country}
                      className="w-full"
                      classNamePrefix="react-select"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <Select
                      options={cityOptions}
                      value={cityOptions.find((opt) => opt.value === form.city) || null}
                      onChange={handleCityChange}
                      placeholder="Select a city (optional)"
                      isClearable
                      isDisabled={!form.state}
                      className="w-full"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Address"
                      name="address"
                      type="text"
                      value={form.address}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Street"
                      name="street"
                      type="text"
                      value={form.street}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Postal Code"
                      name="postalCode"
                      type="text"
                      value={form.postalCode}
                      onChange={handleInputChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <PhoneInput
                      country={'us'}
                      value={form.phone}
                      onChange={handlePhoneChange}
                      placeholder="Enter phone number"
                      inputClass="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500"
                      dropdownClass="border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Verification</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FormInput
                      label="Active"
                      name="active"
                      type="checkbox"
                      checked={form.active}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <FormInput
                      label="Verified"
                      name="isVerified"
                      type="checkbox"
                      checked={form.isVerified}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsCollectorModalOpen(false);
                    setCollectorDetails(null);
                    setForm({
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      address: '',
                      street: '',
                      postalCode: '',
                      country: '',
                      state: '',
                      city: '',
                      active: true,
                      isVerified: false,
                    });
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <FaEdit /> Update
                </button>
              </div>
            </form>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">Loading collector details...</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminBidPage;